"use client";

import { ArrowRight, Calculator, Wallet, BookOpen, ChartLine as LineChart, Compass, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    number: "01",
    title: "OTA Revenue Management",
    description: "Optimize your channel performance and pricing to capture more bookings and increase RevPAR.",
    Icon: Calculator
  }, {
    number: "02",
    title: "Tech Implementation",
    description: "Expert guidance on selecting and integrating the right hospitality tech stack for your property.",
    Icon: Wallet
  }, {
    number: "03",
    title: "Revenue Audits",
    description: "Comprehensive analysis of your current revenue streams to identify hidden growth opportunities.",
    Icon: BookOpen
  }, {
    number: "04",
    title: "Market Intelligence",
    description: "Data-driven insights into local demand patterns to keep your property ahead of competitors.",
    Icon: LineChart
  }, {
    number: "05",
    title: "Strategic Consulting",
    description: "Tailored revenue strategies designed to solve your specific property demand challenges.",
    Icon: Compass
  }, {
    number: "06",
    title: "Performance Tracking",
    description: "Continuous monitoring and reporting to ensure your revenue goals are consistently met.",
    Icon: ShieldCheck
  }];
  return <section data-section-id="241" id="otamanagementoverview" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-serif italic text-base text-muted-foreground/70">02</span>
              <span className="h-px w-8 bg-border" />
              <span className="font-mono uppercase tracking-widest text-xs text-muted-foreground">
                Our Services
              </span>
            </div>
            <h2 className="font-default font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              Revenue solutions for modern{" "}
              <span className="italic font-light text-primary">hotels.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-md lg:ml-auto">
              We help hotel owners and managers maximize profitability through data-driven 
              strategies and expert technology implementation.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/60">
          {services.map((service, i) => {
          const Icon = service.Icon;
          return <div key={i} data-index={i} className={`group relative bg-card p-8 md:p-10 border border-transparent hover:border-primary transition-all duration-500 ease-out hover:-translate-y-0.5 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: gridVisible ? `${i * 80}ms` : "0ms"
          }}>
                {}
                <span className="absolute top-6 right-6 font-serif italic text-3xl text-muted-foreground/30 group-hover:text-primary transition-colors duration-500">
                  {service.number}
                </span>

                {}
                <div className="mb-10">
                  <Icon className="w-10 h-10 text-primary" strokeWidth={1.25} aria-hidden="true" />
                </div>

                {}
                <h3 className="font-default font-serif text-2xl font-normal text-foreground mb-3 leading-snug">
                  {service.title}
                </h3>

                {}
                <p className="text-sm md:text-base leading-relaxed text-muted-foreground mb-8 max-w-sm">
                  {service.description}
                </p>

                {}
                <Link to="/ServicesPage" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-foreground hover:text-primary transition-colors duration-300">
                  <span>Learn more</span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>;
        })}
        </div>

        {}
        <div ref={ctaRef} className={`mt-16 md:mt-20 text-center transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button asChild variant="link" className="text-foreground hover:text-primary font-mono uppercase tracking-wider text-sm h-auto p-0">
            <Link to="/ServicesPage" className="inline-flex items-center gap-2 group">
              <span className="border-b border-border group-hover:border-primary pb-1 transition-colors duration-300">
                View all services
              </span>
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}